import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Table, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';

/**
 * User Dashboard component for displaying user's bookings and account overview.
 * 
 * This component provides a comprehensive dashboard with:
 * - Recent bookings overview
 * - Quick stats
 * - Quick actions
 * - Account information
 * 
 * @author BagABoard Team
 */
function Dashboard() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    activeBookings: 0,
    completedBookings: 0,
    totalSpent: 0
  });

  const { user } = useAuth();

  useEffect(() => {
    loadDashboardData();
  }, []);

  /**
   * Load dashboard data including bookings and statistics
   */
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const bookingsData = await apiService.bookings.getUserBookings();
      console.log('Dashboard bookings data:', bookingsData);
      setBookings(bookingsData.bookings || []);
      
      // Calculate stats from bookings
      const totalBookings = bookingsData.bookings?.length || 0;
      const activeBookings = bookingsData.bookings?.filter(b => 
        ['PENDING', 'CONFIRMED', 'COLLECTED', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(b.status)
      ).length || 0;
      const completedBookings = bookingsData.bookings?.filter(b => b.status === 'DELIVERED').length || 0;
      const totalSpent = bookingsData.bookings?.reduce((sum, b) => sum + (b.totalCost || 0), 0) || 0;
      
      setStats({
        totalBookings,
        activeBookings,
        completedBookings,
        totalSpent
      });
    } catch (error) {
      console.error('Dashboard data loading error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get status badge variant based on booking status
   */
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'CONFIRMED':
        return 'info';
      case 'COLLECTED':
      case 'IN_TRANSIT':
        return 'primary';
      case 'OUT_FOR_DELIVERY':
        return 'success';
      case 'DELIVERED':
        return 'success';
      case 'CANCELLED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  /**
   * Format status for display
   */
  const formatStatus = (status) => {
    return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading your dashboard...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      {/* Welcome Header */}
      <Row className="mb-4">
        <Col>
          <h1 className="display-5 fw-bold">Welcome back, {user?.fullName || 'User'}!</h1>
          <p className="lead text-muted">Here's an overview of your shipping activity</p>
        </Col>
      </Row>

      {/* Quick Stats */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <div className="display-4 text-primary mb-2">
                <i className="fas fa-box"></i>
              </div>
              <h3 className="fw-bold">{stats.totalBookings}</h3>
              <p className="text-muted mb-0">Total Bookings</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <div className="display-4 text-warning mb-2">
                <i className="fas fa-shipping-fast"></i>
              </div>
              <h3 className="fw-bold">{stats.activeBookings}</h3>
              <p className="text-muted mb-0">Active Shipments</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <div className="display-4 text-success mb-2">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3 className="fw-bold">{stats.completedBookings}</h3>
              <p className="text-muted mb-0">Delivered</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <div className="display-4 text-info mb-2">
                <i className="fas fa-dollar-sign"></i>
              </div>
              <h3 className="fw-bold">${stats.totalSpent.toFixed(2)}</h3>
              <p className="text-muted mb-0">Total Spent</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Quick Actions</h5>
              <div className="d-flex gap-3 flex-wrap">
                <Link to="/route-prices" className="btn btn-primary">
                  <i className="fas fa-plus me-2"></i>
                  New Booking
                </Link>
                <Link to="/track" className="btn btn-outline-primary">
                  <i className="fas fa-search me-2"></i>
                  Track Package
                </Link>
                <Link to="/bookings" className="btn btn-outline-secondary">
                  <i className="fas fa-list me-2"></i>
                  All Bookings
                </Link>
                <Link to="/profile" className="btn btn-outline-secondary">
                  <i className="fas fa-user me-2"></i>
                  Profile Settings
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Bookings */}
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Bookings</h5>
                <Link to="/bookings" className="btn btn-sm btn-outline-primary">
                  View All
                </Link>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {bookings.length === 0 ? (
                <div className="text-center py-5">
                  <div className="display-1 text-muted mb-3">
                    <i className="fas fa-inbox"></i>
                  </div>
                  <h4>No Bookings Yet</h4>
                  <p className="text-muted mb-4">
                    You haven't made any bookings yet. Start shipping your bags worldwide!
                  </p>
                  <Link to="/book" className="btn btn-primary">
                    <i className="fas fa-plus me-2"></i>
                    Create Your First Booking
                  </Link>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Reference</th>
                        <th>Route</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Cost</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.slice(0, 5).map(booking => (
                        <tr key={booking.id}>
                          <td>
                            <strong>{booking.bookingReference}</strong>
                          </td>
                          <td>
                            <div>
                              <small className="text-muted">
                                {booking.pickupCity} → {booking.deliveryCity}
                              </small>
                            </div>
                          </td>
                          <td>
                            <Badge bg={getStatusBadgeVariant(booking.status)}>
                              {formatStatus(booking.status)}
                            </Badge>
                          </td>
                          <td>
                            <small>{formatDate(booking.createdAt)}</small>
                          </td>
                          <td>
                            <strong>${booking.totalCost?.toFixed(2) || '0.00'}</strong>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => window.open(`/track?ref=${booking.bookingReference}`, '_blank')}
                              >
                                <i className="fas fa-eye"></i>
                              </Button>
                              {booking.status === 'PENDING' && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to cancel this booking?')) {
                                      // Handle cancellation
                                      toast.info('Cancellation feature coming soon');
                                    }
                                  }}
                                >
                                  <i className="fas fa-times"></i>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Help Section */}
      <Row className="mt-4">
        <Col>
          <Alert variant="info" className="border-0">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <i className="fas fa-info-circle fa-2x"></i>
              </div>
              <div>
                <h6 className="mb-1">Need Help?</h6>
                <p className="mb-2">
                  Our customer support team is available 24/7 to assist you with any questions.
                </p>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" href="/contact">
                    Contact Support
                  </Button>
                  <Button variant="outline-primary" size="sm" href="tel:+15551234567">
                    Call Us
                  </Button>
                </div>
              </div>
            </div>
          </Alert>
        </Col>
      </Row>
    </Container>
  );
}

export default Dashboard;
